import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container, Card, CardContent, Typography, Box, Button, Chip, Alert, CircularProgress, Divider,
} from '@mui/material';
import { VerifiedUser, LocationOn, Schedule, Paid } from '@mui/icons-material';
import contractorVerificationService from '../services/contractorVerificationService';
import { brand, text, status as statusTokens } from '../theme/colors';

/**
 * One vetting job on offer. Opened from the offer notification (email /
 * WhatsApp / in-app). First verifier to accept gets the file and lands on
 * the normal assignment page; everyone else's offer is superseded.
 */
const VerifierVettingOfferPage = () => {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    contractorVerificationService.getVettingOffer(offerId)
      .then((r) => { if (alive) setOffer(r.data); })
      .catch((e) => { if (alive) setError(e.response?.data?.message || 'This offer could not be loaded.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [offerId]);

  const accept = async () => {
    setBusy(true); setError('');
    try {
      const r = await contractorVerificationService.acceptVettingOffer(offerId);
      const assignmentId = r.data?.assignment?.id || r.data?.assignmentId;
      navigate(assignmentId ? `/verifier/contractor-files/${assignmentId}` : '/verifier/my-work');
    } catch (e) {
      setError(e.response?.data?.message || 'Could not accept this job.');
      setBusy(false);
    }
  };

  const decline = async () => {
    setBusy(true); setError('');
    try {
      await contractorVerificationService.declineVettingOffer(offerId);
      setOffer((o) => ({ ...o, status: 'declined' }));
    } catch (e) {
      setError(e.response?.data?.message || 'Could not decline this job.');
    } finally { setBusy(false); }
  };

  if (loading) return <Container maxWidth="sm" sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Container>;

  const open = offer && offer.status === 'offered';
  const c = offer?.contractor || {};
  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Card>
        <CardContent>
          <Typography variant="overline" sx={{ color: text.tertiary, fontWeight: 700, letterSpacing: '0.08em' }}>Contractor vetting job</Typography>
          {error && <Alert severity="error" sx={{ my: 1.5 }}>{error}</Alert>}
          {!offer ? null : (
            <>
              <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5 }}>{c.companyName || [c.firstName, c.lastName].filter(Boolean).join(' ') || 'A contractor'}</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                {(c.city || c.province) && <Chip size="small" icon={<LocationOn />} label={[c.suburb, c.city, c.province].filter(Boolean).join(', ')} />}
                <Chip size="small" icon={<VerifiedUser />} label={`${offer.requiredSiteVisits || 2} site visit${(offer.requiredSiteVisits || 2) === 1 ? '' : 's'} with GPS proof`} />
                {offer.fee != null && <Chip size="small" icon={<Paid />} label={`USD ${offer.fee} paid on submission`} sx={{ bgcolor: statusTokens.successBg, color: statusTokens.successDark, fontWeight: 600 }} />}
                {offer.expiresAt && open && <Chip size="small" icon={<Schedule />} label={`Offer open until ${new Date(offer.expiresAt).toLocaleString('en-GB', { timeZone: 'Africa/Harare', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}`} />}
              </Box>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" sx={{ color: text.secondary }}>
                You check this contractor in person: confirm the business and its representative, visit past work sites (geofenced photo proof), and call their references. Then you submit a recommendation. Eqaya's clients see "Field-verified by an Eqaya verifier" on the contractor's profile because of your visit.
              </Typography>
              {offer.status === 'accepted' && <Alert severity="success" sx={{ mt: 2 }}>You accepted this job. Find it under My work.</Alert>}
              {offer.status === 'declined' && <Alert severity="info" sx={{ mt: 2 }}>You declined this job. Thanks for letting us know.</Alert>}
              {['expired', 'superseded'].includes(offer.status) && <Alert severity="warning" sx={{ mt: 2 }}>This job has already been taken or the offer has expired. New offers arrive as jobs come up.</Alert>}
              {open && (
                <Box sx={{ display: 'flex', gap: 1.5, mt: 3 }}>
                  <Button variant="contained" size="large" onClick={accept} disabled={busy} sx={{ bgcolor: brand.primary }}>Accept this job</Button>
                  <Button variant="text" onClick={decline} disabled={busy}>Not this one</Button>
                </Box>
              )}
              <Typography variant="caption" sx={{ display: 'block', mt: 2, color: text.tertiary }}>First verifier to accept gets the file. If it's not for you, declining helps it reach someone who can do it.</Typography>
            </>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default VerifierVettingOfferPage;
